'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function Base64Tool() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState('encode'); // 'encode' or 'decode'

  const handleEncode = () => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(input)));
      setOutput(encoded);
      setError('');
    } catch (err) {
      setError(`Fehler beim Kodieren: ${err.message}`);
      setOutput('');
    }
  };

  const handleDecode = () => {
    try {
      const decoded = decodeURIComponent(escape(atob(input)));
      setOutput(decoded);
      setError('');
    } catch (err) {
      setError(`Fehler beim Dekodieren: ${err.message}. Stelle sicher, dass der Input valides Base64 ist.`);
      setOutput('');
    }
  };

  const copyToClipboard = async () => {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const loadExample = () => {
    if (mode === 'encode') {
      setInput('Hallo Welt! Dies ist ein Beispieltext mit Umlauten: äöü');
    } else {
      setInput('SGFsbG8gV2VsdCEgRGllcyBpc3QgZWluIEJlaXNwaWVsdGV4dCBtaXQgVW1sYXV0ZW46IMOkw7bDvA==');
    }
    setOutput('');
    setError('');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Base64 Encoder / Decoder
      </h3>
      <p className="text-gray-700 mb-6">
        Kodiere Text zu Base64 oder dekodiere Base64 zurück zu Text. Unterstützt UTF-8 und Umlaute.
      </p>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => {
            setMode('encode');
            setInput('');
            setOutput('');
            setError('');
          }}
          className={cn(
            'flex-1 px-6 py-3 rounded-lg font-semibold transition-colors',
            mode === 'encode'
              ? 'bg-accent text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          )}
        >
          Kodieren
        </button>
        <button
          onClick={() => {
            setMode('decode');
            setInput('');
            setOutput('');
            setError('');
          }}
          className={cn(
            'flex-1 px-6 py-3 rounded-lg font-semibold transition-colors',
            mode === 'decode'
              ? 'bg-accent text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          )}
        >
          Dekodieren
        </button>
      </div>

      {/* Input Section */}
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="base64-input" className="block text-sm font-semibold">
              {mode === 'encode' ? 'Text Eingabe' : 'Base64 Eingabe'}
            </label>
            <button
              onClick={loadExample}
              className="text-sm text-accent hover:text-accent-dark font-semibold"
            >
              Beispiel laden
            </button>
          </div>
          <textarea
            id="base64-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === 'encode' ? 'Gib deinen Text ein...' : 'Gib deinen Base64-String ein...'}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm min-h-[200px]"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={mode === 'encode' ? handleEncode : handleDecode}
            className="bg-accent hover:bg-accent-dark text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            {mode === 'encode' ? 'Kodieren' : 'Dekodieren'}
          </button>
          <button
            onClick={clearAll}
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Leeren
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">
            <strong>⚠️ Fehler:</strong> {error}
          </p>
        </div>
      )}

      {/* Output Section */}
      {output && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold">
                Ergebnis
              </label>
              <button
                onClick={copyToClipboard}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-accent hover:bg-accent-dark text-white'
                )}
              >
                {copied ? '✓ Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm font-mono whitespace-pre-wrap break-all">
                {output}
              </pre>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Hinweis:</strong> Alle Daten werden nur lokal in deinem Browser verarbeitet.
              Nichts wird an einen Server gesendet.
            </p>
          </div>
        </div>
      )}

      {!output && !error && (
        <div className="text-center text-gray-500 py-8">
          Füge Text oder Base64 ein und klicke auf {mode === 'encode' ? 'Kodieren' : 'Dekodieren'}
        </div>
      )}

      {/* Feature List */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Features</h4>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>UTF-8 Support:</strong> Volle Unterstützung für Umlaute und Sonderzeichen
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Bidirektional:</strong> Kodieren und Dekodieren in einem Tool
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Fehlerbehandlung:</strong> Detaillierte Fehlermeldungen bei ungültigem Input
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>100% Client-Side:</strong> Deine Daten verlassen niemals deinen Browser
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
